// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Which fields the Ressourcer type inspector and Tabel › Typer show: one of
 * the project's templates (the seeds are templates like any other), or the
 * whole catalogue ("Alle felter"). The choice travels in `?fields=` and is
 * remembered per project (`kortlaegning/templatePick`); this module is its
 * wire form, the fallback when the wanted template is gone, and the picker's
 * options. Pure: no React, no DOM.
 */

import type { Template } from '../api/types';
import { templateDisplayName } from '../skabeloner/model';

export type FieldView = { kind: 'template'; id: number } | { kind: 'all' };

export const ALL_FIELDS: FieldView = { kind: 'all' };

const ALL_WIRE = 'all';
const ALL_LABEL = 'Alle felter';

/** Wire form for `?fields=` and localStorage: 'all' | '<id>'. Null for junk. */
export function parseFieldView(raw: string | null | undefined): FieldView | null {
  if (raw === null || raw === undefined) return null;
  if (raw === ALL_WIRE) return ALL_FIELDS;
  if (!/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) ? { kind: 'template', id } : null;
}

export function formatFieldView(v: FieldView): string {
  return v.kind === 'all' ? ALL_WIRE : String(v.id);
}

export function sameFieldView(a: FieldView | null, b: FieldView | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind === 'all' || b.kind === 'all') return a.kind === b.kind;
  return a.id === b.id;
}

/** 'all' or an existing template → itself; else screening seed → first template → ALL_FIELDS. */
export function resolveFieldView(wanted: FieldView | null, templates: readonly Template[]): FieldView {
  if (wanted !== null) {
    if (wanted.kind === 'all') return ALL_FIELDS;
    const { id } = wanted;
    if (templates.some((t) => t.id === id)) return wanted;
  }
  const fallback = templates.find((t) => t.seed === 'screening') ?? templates[0];
  return fallback ? { kind: 'template', id: fallback.id } : ALL_FIELDS;
}

/** The template a view reads; null for Alle felter or an id the project no longer has. */
export function viewTemplate(view: FieldView, templates: readonly Template[]): Template | null {
  if (view.kind === 'all') return null;
  const { id } = view;
  return templates.find((t) => t.id === id) ?? null;
}

/** Picker options: screening seed, materialepas seed, other templates in server order, then Alle felter. */
export function fieldViewOptions(templates: readonly Template[]): { value: string; label: string }[] {
  const rank = (t: Template) => (t.seed === 'screening' ? 0 : t.seed === 'materialepas' ? 1 : 2);
  // Array.prototype.sort is stable: the other templates keep the server's order.
  const ordered = [...templates].sort((a, b) => rank(a) - rank(b));
  return [
    ...ordered.map((t) => ({ value: formatFieldView({ kind: 'template', id: t.id }), label: templateDisplayName(t) })),
    { value: formatFieldView(ALL_FIELDS), label: ALL_LABEL },
  ];
}
